/**
 * Mercancía Invoice PTB: agrupa por producto + medida + grado (sin piezas/paquete).
 * Precios desde el Excel comercial; no altera agrupación PTC ni el packing list.
 */
import {
  aggregateCommercialPricingForRows,
  commercialLineasTienenPrecios,
} from './commercialPackingListPricing';
import {
  dimensionLineFromPackingRow,
  etiquetaPtbForPackingRow,
  gradoConsolidadoFromPackingRow,
  isRwNrPackingRow,
  parseDimNumber,
  productNameFromPackingRow,
  ptbDimsFromPackingRow,
  comparePtcMerchandiseLines,
  piezasPorPaqueteMerchandise,
  round2,
  round3,
} from './invoicePtcMerchandise';

function safe(v) {
  return v == null ? '' : String(v);
}

/** Solo clave de agrupación PTB (no altera M³ ni USD). Unifica metros vs mm en ancho/largo. */
function normDimKeyPart(v, role = 'default') {
  const s = safe(v).trim().toUpperCase().replace(/\s+/g, ' ');
  if (!s) return '';
  if (/^RW$/i.test(s)) return 'RW';
  const n = parseDimNumber(v);
  if (n == null) return s;
  if (role === 'espesor') {
    if (Math.abs(n - Math.round(n)) < 1e-6) return String(Math.round(n));
    return String(n);
  }
  if (n >= 100) return String(Math.round(n));
  if (n > 0 && n < 100) return String(Math.round(n * 1000));
  return String(n);
}

/**
 * Clave PTB: producto + grado + medida (sin piezas/paquete).
 */
export function ptbMeasureGradeKey(row) {
  const grado = gradoConsolidadoFromPackingRow(row) || '';
  const dims = ptbDimsFromPackingRow(row);
  return [
    safe(row?.productName || productNameFromPackingRow(row)).trim().toUpperCase().replace(/\s+/g, ' '),
    grado,
    normDimKeyPart(dims.espessura || row?.espesor, 'espesor'),
    normDimKeyPart(dims.largura || row?.ancho, 'ancho'),
    normDimKeyPart(dims.comprimento || row?.largo, 'largo'),
  ].join('|');
}

export function groupCommercialRowsByMeasureGrade(lineas) {
  const groups = new Map();
  for (const row of lineas || []) {
    const m3 = Number(row?.total_m3);
    if (!Number.isFinite(m3) || m3 <= 0) continue;
    const key = ptbMeasureGradeKey(row);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }
  return groups;
}

function descriptionBlockPtb(rows, productDescriptionSnapshot) {
  const sample = rows[0] || {};
  const head =
    safe(productDescriptionSnapshot).trim() || productNameFromPackingRow(sample) || '—';
  const dim = dimensionLineFromPackingRow(sample);
  let sumBundles = 0;
  let sumPiezas = 0;
  for (const row of rows) {
    const b = Number(row?.cantidad_paquetes);
    if (Number.isFinite(b)) sumBundles += b;
    const p = Number(row?.total_piezas);
    if (Number.isFinite(p)) sumPiezas += p;
  }
  const piezasPaq = piezasPorPaqueteMerchandise(sample);
  const grado = gradoConsolidadoFromPackingRow(sample);
  const lines = [head];
  if (dim) lines.push(dim);
  if (sumBundles > 0) lines.push(`Bundles ${Math.round(sumBundles)}`);
  if (piezasPaq && grado !== 'RW') lines.push(`Pieces/Bundles ${piezasPaq}`);
  if (sumPiezas > 0 && !isRwNrPackingRow(sample)) {
    lines.push(`Pieces ${Math.round(sumPiezas)}`);
  }
  return lines.join('\n');
}

/**
 * @param {Array} commercialLineas - líneas del packing list comercial (invoice)
 * @param {object|null} _preciosPtbLegacy - ignorado; precios vienen en commercialLineas
 * @param {object|null} proforma - PI (etiquetas / descripción; no precios PTB)
 */
export function buildInvoicePtbMerchandise(commercialLineas, _preciosPtbLegacy, proforma, opts = {}) {
  const currency = safe(opts.currency || 'USD').trim().toUpperCase() || 'USD';
  const rowsByKey = groupCommercialRowsByMeasureGrade(commercialLineas);
  const hasPrecios = commercialLineasTienenPrecios(commercialLineas);

  let totalM3 = 0;
  let totalBundles = 0;
  let totalUsd = 0;
  const lineasMercancia = [];

  for (const rows of rowsByKey.values()) {
    const sample = rows[0];
    let sumM3 = 0;
    let sumBundles = 0;
    let sumPiezas = 0;
    for (const row of rows) {
      const m3 = Number(row?.total_m3);
      if (Number.isFinite(m3) && m3 > 0) sumM3 += m3;
      const b = Number(row?.cantidad_paquetes);
      if (Number.isFinite(b)) sumBundles += b;
      const p = Number(row?.total_piezas);
      if (Number.isFinite(p)) sumPiezas += p;
    }

    const quantityM3 = round3(sumM3);
    const agg = hasPrecios
      ? aggregateCommercialPricingForRows(rows)
      : { sumM3: 0, sumUsd: 0, unitPriceUsd: null };

    const unitPriceUsd = hasPrecios ? agg.unitPriceUsd : null;
    const totalValueUsd = hasPrecios
      ? agg.sumUsd > 0
        ? agg.sumUsd
        : unitPriceUsd != null && quantityM3 > 0
          ? round2(quantityM3 * unitPriceUsd)
          : null
      : null;

    totalM3 += quantityM3;
    totalBundles += sumBundles;
    if (totalValueUsd != null) totalUsd += totalValueUsd;

    const ptbDims = ptbDimsFromPackingRow(sample);
    lineasMercancia.push({
      quantityM3,
      productName: productNameFromPackingRow(sample),
      grado: gradoConsolidadoFromPackingRow(sample) || '',
      internalCode: '',
      descriptionBlock: descriptionBlockPtb(rows, opts.productDescriptionSnapshot),
      etiquetaPtb: etiquetaPtbForPackingRow(sample, proforma, {
        ...opts,
        productName: productNameFromPackingRow(sample),
      }),
      espessura: ptbDims.espessura,
      largura: ptbDims.largura,
      comprimento: ptbDims.comprimento,
      piezasPorPaquete: piezasPorPaqueteMerchandise(sample),
      bundles: round3(sumBundles),
      totalPiezas: round3(sumPiezas),
      unitPriceUsd,
      totalValueUsd,
      currency,
      djcNum: safe(opts.numeroInvoice),
      djcDate: opts.fechaInvoice || opts.fechaEmbarque || '',
      sampleRow: sample,
    });
  }

  lineasMercancia.sort(comparePtcMerchandiseLines);

  return {
    lineasMercancia,
    totalM3: round3(totalM3),
    totalBundles: round3(totalBundles),
    totalUsd: round2(totalUsd),
    currency,
  };
}

function ptbLineMatchKey(ln) {
  return [
    safe(ln?.productName || productNameFromPackingRow(ln)).trim().toUpperCase().replace(/\s+/g, ' '),
    gradoConsolidadoFromPackingRow(ln),
    safe(ln?.espessura).trim(),
    safe(ln?.largura).trim().toUpperCase(),
    safe(ln?.comprimento).trim(),
  ].join('|');
}

/**
 * Aplica unitarios confirmados en el formulario sobre la mercancía PTB recalculada del PL.
 * Conserva M³/estructura automática; el total de línea usa el precio del form.
 */
export function applyPtbFormUnitPrices(autoLineas, formLineas) {
  const form = Array.isArray(formLineas) ? formLineas : [];
  if (!form.length) return autoLineas || [];

  const byKey = new Map();
  for (const ln of form) {
    const key = ptbLineMatchKey(ln);
    if (key && key !== '|||') {
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(ln);
    }
  }

  return (autoLineas || []).map((auto) => {
    const matches = byKey.get(ptbLineMatchKey(auto)) || [];
    if (!matches.length) return auto;

    let sumM3 = 0;
    let sumUsd = 0;
    const unitPrices = [];
    for (const match of matches) {
      const raw = match.unitPriceUsd ?? match.unitPrice;
      const unit = raw == null || raw === '' ? null : Number(raw);
      const qty = Number(match.quantityM3);
      const lineTotal =
        match.totalValueUsd == null || match.totalValueUsd === ''
          ? null
          : Number(match.totalValueUsd);
      if (Number.isFinite(unit)) unitPrices.push(unit);
      if (Number.isFinite(qty) && qty > 0 && Number.isFinite(lineTotal)) {
        sumM3 += qty;
        sumUsd += lineTotal;
      } else if (Number.isFinite(qty) && qty > 0 && Number.isFinite(unit)) {
        sumM3 += qty;
        sumUsd += qty * unit;
      }
    }

    const unitPriceUsd = sumM3 > 0
      ? round2(sumUsd / sumM3)
      : unitPrices.length
        ? round2(unitPrices[unitPrices.length - 1])
        : null;
    if (unitPriceUsd == null) return auto;
    const qty = Number(auto.quantityM3);
    const totalValueUsd =
      sumUsd > 0
        ? round2(sumUsd)
        : Number.isFinite(qty) && qty > 0 && unitPriceUsd != null
          ? round2(qty * unitPriceUsd)
          : null;
    return { ...auto, unitPriceUsd, totalValueUsd };
  });
}

/** Total USD PTB desde líneas (tras overlay del formulario). */
export function totalUsdFromPtbLineas(lineas) {
  const sum = (lineas || []).reduce(
    (s, ln) => s + (Number(ln?.totalValueUsd) || 0),
    0
  );
  return round2(sum);
}
